// /opt/dashboard/frontend/src/pages/ActivityMap.js
// Patch 399 — Live Activity Map (POI consume tranzakciók térképen)

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.heat'; // L.heatLayer regisztrálása side-effect import
import 'leaflet/dist/leaflet.css';
import { TILE_URL, TILE_URL_LIGHT, TILE_ATTRIBUTION } from '../components/basemaps';
import api from '../api';

const C = {
  bg: '#1a1d27',
  panel: '#22252f',
  border: '#2a2e3a',
  text: '#e0e6ed',
  muted: '#8892a0',
  accent: '#00d4ff',
  warning: '#fbbf24',
  danger: '#ef4444',
  success: '#22c55e',
};

const POLL_INTERVAL_MS = 10000; // 10 sec

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('hu-HU', { dateStyle: 'short', timeStyle: 'medium' });
  } catch {
    return iso;
  }
}

function relativeTime(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

// Időalapú szín: friss = élénk, régi = halványabb. A VILÁGOS térképen
// telítettebb, sötétebb kék + magasabb opacitás-alsóhatár, hogy a pontok ne
// olvadjanak bele a világoskék vízbe.
function colorForAge(iso, days, basemap) {
  if (!iso) return C.muted;
  const ageMinutes = (Date.now() - new Date(iso).getTime()) / 60000;
  const totalMinutes = days * 24 * 60;
  const ratio = Math.min(ageMinutes / totalMinutes, 1);
  // 1 perc alatt: piros (új), 30 perc: sárga/narancs, 1h+: fakuló kék
  if (ageMinutes < 1) return '#ff3366';
  if (ageMinutes < 30) return '#f59e0b';
  if (basemap === 'light') {
    const opacity = 1 - ratio * 0.4; // 1.0 → 0.6 (nem tűnik el a világos háttéren)
    return '#1d4ed8' + Math.floor(opacity * 255).toString(16).padStart(2, '0');
  }
  const opacity = 1 - ratio * 0.7;
  return C.accent + Math.floor(opacity * 255).toString(16).padStart(2, '0');
}

function radiusForAge(iso) {
  if (!iso) return 6;
  const ageMinutes = (Date.now() - new Date(iso).getTime()) / 60000;
  if (ageMinutes < 1) return 12; // pulse-szerűen nagyobb friss
  if (ageMinutes < 5) return 10;
  if (ageMinutes < 60) return 8;
  return 6;
}

// Auto-fit a betöltött pontokra (csak első alkalommal, amikor megjelennek pontok)
function FitBounds({ points }) {
  const map = useMap();
  const fittedRef = useRef(false);
  useEffect(() => {
    if (!points.length || fittedRef.current) return;
    const bounds = points.map(p => [p.poiLat, p.poiLon]);
    if (bounds.length === 1) {
      map.setView(bounds[0], 12);
    } else {
      try {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
      } catch {
        // ignore
      }
    }
    fittedRef.current = true;
  }, [points, map]);
  return null;
}

// 🆕 Hőtérkép-réteg — gyakorisági sűrűséget jelenít meg a tranzakciókból
function HeatmapLayer({ points }) {
  const map = useMap();
  const layerRef = useRef(null);
  useEffect(() => {
    if (!points || points.length === 0) {
      if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; }
      return;
    }
    // a heat-pontok: [lat, lon, intensity]. Itt minden tranzakció +1 intenzitást ad.
    // Ugyanazon POI több tranzakciója természetszerűen halmozódik.
    const heatPoints = points.map(p => [p.poiLat, p.poiLon, 1]);
    if (layerRef.current) {
      layerRef.current.setLatLngs(heatPoints);
    } else {
      layerRef.current = L.heatLayer(heatPoints, {
        radius: 25,
        blur: 18,
        maxZoom: 14,
        gradient: { 0.2: '#00d4ff', 0.4: '#22c55e', 0.6: '#fbbf24', 0.8: '#fb923c', 1.0: '#ef4444' },
      }).addTo(map);
    }
    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [points, map]);
  return null;
}

export default function ActivityMap() {
  const [days, setDays] = useState(1);
  // 🆕 viewMode: 'markers' | 'heatmap' — egyaránt mehetne overlay-ként, de toggle tisztább
  const [viewMode, setViewMode] = useState('markers');
  const [basemap, setBasemap] = useState('dark'); // alaptérkép: 'dark' | 'light'
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [livePulse, setLivePulse] = useState(false);
  const [, setTick] = useState(0); // re-render trigger to update marker colors/sizes (5s interval)
  const lastFetchRef = useRef(null);
  const pollTimerRef = useRef(null);

  const initialLoad = useCallback(async (d) => {
    setLoading(true);
    setError(null);
    try {
      const j = await api.activityMap.recent(d);
      setTransactions(j.transactions || []);
      lastFetchRef.current = new Date().toISOString();
    } catch (e) {
      setError(e.message || 'Hiba');
    } finally {
      setLoading(false);
    }
  }, []);

  const pollNew = useCallback(async () => {
    if (!lastFetchRef.current) return;
    try {
      const j = await api.activityMap.since(lastFetchRef.current);
      const fresh = j.transactions || [];
      if (fresh.length > 0) {
        setLivePulse(true);
        setTimeout(() => setLivePulse(false), 1500);
        setTransactions(prev => {
          // Dedup id alapján
          const ids = new Set(prev.map(p => p.id));
          const newOnes = fresh.filter(f => !ids.has(f.id));
          return [...newOnes, ...prev];
        });
        lastFetchRef.current = new Date().toISOString();
      } else {
        lastFetchRef.current = new Date().toISOString();
      }
    } catch (e) {
      // silent fail — folyamatos polling
      console.warn('Polling error:', e.message);
    }
  }, []);

  // Initial load
  useEffect(() => {
    initialLoad(days);
  }, [days, initialLoad]);

  // Polling timer
  useEffect(() => {
    pollTimerRef.current = setInterval(pollNew, POLL_INTERVAL_MS);
    return () => clearInterval(pollTimerRef.current);
  }, [pollNew]);

  // Color/radius refresh tick (5 sec → markerek "öregednek")
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 5000);
    return () => clearInterval(t);
  }, []);

  // Stats
  const uniqueUsers = new Set(transactions.map(t => t.userId)).size;
  const uniqueLangs = new Set(transactions.map(t => t.poiLang).filter(Boolean));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)', background: C.bg, color: C.text }}>
      {/* Top bar */}
      <div style={{ padding: '12px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, color: C.accent, fontSize: 18 }}>🌍 Live Activity Map</h2>

        <div style={{ display: 'flex', gap: 4 }}>
          {[1, 7, 30].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              style={{
                padding: '6px 12px',
                background: days === d ? C.accent : C.border,
                color: days === d ? '#000' : C.text,
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: days === d ? 'bold' : 'normal',
              }}
            >
              {d === 1 ? '24h' : `${d}d`}
            </button>
          ))}
        </div>

        {/* 🆕 Nézet váltás: markerek / hőtérkép */}
        <div style={{ display: 'flex', gap: 4 }}>
          {[['markers', '📍 Markerek'], ['heatmap', '🔥 Hőtérkép']].map(([v, label]) => (
            <button
              key={v}
              onClick={() => setViewMode(v)}
              style={{
                padding: '6px 12px',
                background: viewMode === v ? C.accent : C.border,
                color: viewMode === v ? '#000' : C.text,
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: viewMode === v ? 'bold' : 'normal',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* 🆕 Alaptérkép váltás: sötét / Voyager */}
        <div style={{ display: 'flex', gap: 4 }}>
          {[['dark', '🌑 Sötét'], ['light', '🗺️ Világos']].map(([v, label]) => (
            <button
              key={v}
              onClick={() => setBasemap(v)}
              style={{
                padding: '6px 12px',
                background: basemap === v ? C.accent : C.border,
                color: basemap === v ? '#000' : C.text,
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: basemap === v ? 'bold' : 'normal',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginLeft: 'auto', fontSize: 12, color: C.muted }}>
          <div>
            <span style={{ color: C.text, fontWeight: 'bold' }}>{transactions.length}</span> tranzakció
          </div>
          <div>
            <span style={{ color: C.text, fontWeight: 'bold' }}>{uniqueUsers}</span> user
          </div>
          <div>
            <span style={{ color: C.text, fontWeight: 'bold' }}>{uniqueLangs.size}</span> nyelv
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: livePulse ? C.warning : C.success,
              transition: 'background 0.3s',
              boxShadow: livePulse ? `0 0 8px ${C.warning}` : `0 0 4px ${C.success}`,
            }} />
            {livePulse ? 'NEW' : 'LIVE'}
          </div>
        </div>
      </div>

      {error && (
        <div style={{ padding: 12, background: '#3a1f1f', color: C.danger, fontSize: 13 }}>
          ⚠️ {error}
        </div>
      )}

      {loading && transactions.length === 0 && (
        <div style={{ padding: 20, color: C.muted }}>Betöltés...</div>
      )}

      {/* Map */}
      <div style={{ flex: 1, position: 'relative' }}>
        <MapContainer
          center={[47.5, 19.0]}
          zoom={5}
          style={{ height: '100%', width: '100%', background: C.bg }}
        >
          <TileLayer
            key={basemap}
            url={basemap === 'light' ? TILE_URL_LIGHT : TILE_URL}
            attribution={TILE_ATTRIBUTION}
          />
          <FitBounds points={transactions} />
          {viewMode === 'heatmap' && <HeatmapLayer points={transactions} />}
          {viewMode === 'markers' && transactions.map(t => (
            <CircleMarker
              key={t.id}
              center={[t.poiLat, t.poiLon]}
              radius={radiusForAge(t.timestamp)}
              pathOptions={{
                // Világos térképen sötét kontúr + telítettebb kitöltés a kontraszthoz
                color: basemap === 'light' ? '#0f172a' : colorForAge(t.timestamp, days, basemap),
                fillColor: colorForAge(t.timestamp, days, basemap),
                fillOpacity: basemap === 'light' ? 0.9 : 0.6,
                weight: basemap === 'light' ? 1.2 : 1,
              }}
            >
              <Popup>
                <div style={{ fontSize: 12, fontFamily: 'system-ui, sans-serif', minWidth: 200 }}>
                  <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 4 }}>
                    📍 {t.poiName}
                  </div>
                  <div style={{ color: '#666' }}>
                    {t.poiLat?.toFixed(4)}, {t.poiLon?.toFixed(4)}
                  </div>
                  <hr style={{ margin: '6px 0', border: 'none', borderTop: '1px solid #eee' }} />
                  <div><b>User:</b> {t.userEmail || <code style={{ fontSize: 10 }}>{t.userId.slice(0, 12)}...</code>}</div>
                  {t.poiLang && <div><b>Nyelv:</b> {t.poiLang}</div>}
                  {t.poiType && <div><b>Type:</b> {t.poiType}</div>}
                  <div><b>Idő:</b> {formatDate(t.timestamp)}</div>
                  <div style={{ color: '#999', fontSize: 11, marginTop: 2 }}>
                    {relativeTime(t.timestamp)}
                  </div>
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>

        {/* Legend */}
        <div style={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          background: C.panel,
          padding: 12,
          borderRadius: 6,
          border: `1px solid ${C.border}`,
          fontSize: 11,
          color: C.text,
          zIndex: 1000,
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: 6, color: C.accent }}>Életkor</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
            <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#ff3366' }} />
            <span>&lt; 1 perc (új)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: C.warning }} />
            <span>&lt; 30 perc</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.accent }} />
            <span>régebbi (fakuló)</span>
          </div>
        </div>
      </div>
    </div>
  );
}
